import { Stack } from "@mui/material";
import { type ReactNode, useMemo } from "react";
import { cn } from "utils/styling";

import type {
  RecordFormColumns,
  RecordFormField,
  RecordFormTab,
  RecordFormTabOrientation,
  RecordFormTabPanelPlacement,
} from "./types";

import { DynamicFormTabs } from "./DynamicFormTabs";
import { DynamicRecordFormGrid, DynamicRecordFormSections } from "./DynamicRecordFormSections";
import { getHeaderFields, getTabFields, groupFieldsBySection } from "./lib/layout";

type DynamicRecordDrawerContentProps = {
  activeTabName: string;
  columns?: RecordFormColumns;
  fields: RecordFormField[];
  headerColumns?: RecordFormColumns;
  onTabChange: (tabName: string) => void;
  tabOrientation: RecordFormTabOrientation;
  tabPanelPlacement?: Partial<Record<string, RecordFormTabPanelPlacement>>;
  tabPanels?: Partial<Record<string, ReactNode>>;
  visibleTabs: RecordFormTab[];
};

export const DynamicRecordDrawerContent = ({
  activeTabName,
  columns = 2,
  fields,
  headerColumns = 1,
  onTabChange,
  tabOrientation,
  tabPanelPlacement,
  tabPanels,
  visibleTabs,
}: DynamicRecordDrawerContentProps) => {
  const headerFields = useMemo(() => getHeaderFields(fields), [fields]);
  const activeTabFields = useMemo(
    () => groupFieldsBySection(getTabFields(fields, activeTabName)),
    [activeTabName, fields]
  );
  const isVertical = tabOrientation === "vertical";
  const panel = tabPanels?.[activeTabName];
  const placement = tabPanelPlacement?.[activeTabName] ?? (panel ? "replace" : "after");
  const fieldsContent =
    activeTabFields.length > 0 && placement !== "replace" ? (
      <DynamicRecordFormSections columns={columns} sectionGroups={activeTabFields} />
    ) : null;

  return (
    <Stack className="flex w-full flex-1 flex-col overflow-hidden px-6 py-4">
      {headerFields.length > 0 && <DynamicRecordFormGrid columns={headerColumns} fields={headerFields} />}
      {visibleTabs.length > 0 && (
        <>
          {visibleTabs.length > 1 && !isVertical && (
            <DynamicFormTabs
              activeTabName={activeTabName}
              className="mt-4 w-full"
              onTabChange={onTabChange}
              orientation="horizontal"
              tabs={visibleTabs}
            />
          )}
          <Stack
            className={cn(
              "min-h-0 w-full flex-1 flex-col overflow-y-auto overflow-x-hidden pt-4",
              isVertical && "flex-row items-start gap-6"
            )}
          >
            {visibleTabs.length > 1 && isVertical && (
              <DynamicFormTabs
                activeTabName={activeTabName}
                className="self-stretch"
                onTabChange={onTabChange}
                orientation="vertical"
                tabs={visibleTabs}
              />
            )}
            <div className="min-h-0 w-full min-w-0 flex-1 overflow-y-auto overflow-x-hidden pb-4">
              <Stack className="w-full flex-col gap-6">
                {placement === "after" && fieldsContent}
                {panel}
                {placement === "before" && fieldsContent}
              </Stack>
            </div>
          </Stack>
        </>
      )}
    </Stack>
  );
};
